import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { ReactComponent as ChevronDownIcon } from 'icons/arrow-simple-thick-down.svg';
import useOutsideClick from 'components/hooks/useOutsideClick';
import Tooltip from 'components/common/tool-tip-component';
export const CHATBOT_PROMPT_SELECTOR_BLOCK = 'chatbot-prompt-selector';
export const CHATBOT_PROMPT_SELECTOR_ID = 'chatbot-prompt-selector-id';
const TOOLTIP_ID_BLOCK = 'chatbot-prompt-tooltip';
const ChatBotPromptSelector = ({
  prompts,
  onPromptSelect,
  disabled = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownRef] = useOutsideClick(() => isOpen && setIsOpen(false));

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handlePromptSelect = (prompt) => {
    onPromptSelect(prompt);
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      className={classNames(CHATBOT_PROMPT_SELECTOR_BLOCK, className, {
        [`${CHATBOT_PROMPT_SELECTOR_BLOCK}--disabled`]: disabled,
        [`${CHATBOT_PROMPT_SELECTOR_BLOCK}--open`]: isOpen,
      })}
    >
      <Tooltip
        text="chatbot-body.prompt-library.tooltip"
        id={`${TOOLTIP_ID_BLOCK}-prompt-tooltip`}
        position={'top'}
      >
        <button
          type="button"
          className={`${CHATBOT_PROMPT_SELECTOR_BLOCK}__trigger`}
          onClick={handleToggle}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          id={`${CHATBOT_PROMPT_SELECTOR_ID}__trigger`}
        >
          <div className={`${CHATBOT_PROMPT_SELECTOR_BLOCK}__selected-content`}>
            <div className={`${CHATBOT_PROMPT_SELECTOR_BLOCK}__model-info`}>
              <FormattedMessage id={'chatbot-body.prompt-library'} />
            </div>
            <ChevronDownIcon
              className={classNames(
                `${CHATBOT_PROMPT_SELECTOR_BLOCK}__chevron`,
                {
                  [`${CHATBOT_PROMPT_SELECTOR_BLOCK}__chevron--rotated`]:
                    isOpen,
                },
              )}
              width="12"
              height="12"
            />
          </div>
        </button>
      </Tooltip>
      {isOpen && (
        <div
          className={`${CHATBOT_PROMPT_SELECTOR_BLOCK}__dropdown`}
          role="listbox"
          aria-labelledby={`${CHATBOT_PROMPT_SELECTOR_ID}__trigger`}
        >
          {Array.isArray(prompts) &&
            prompts?.map((prompt, index) => (
              <Tooltip
                key={prompt.promptId}
                id={`${TOOLTIP_ID_BLOCK}__suggested-prompt-${index}`}
                text={prompt.tooltip}
                position={'right'}
                event="click"
              >
                <button
                  type="button"
                  className={`${CHATBOT_PROMPT_SELECTOR_BLOCK}__option`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePromptSelect(prompt);
                  }}
                  role="option"
                >
                  <div
                    className={`${CHATBOT_PROMPT_SELECTOR_BLOCK}__option-name`}
                  >
                    <FormattedMessage id={`${prompt.title}`} />
                  </div>
                </button>
              </Tooltip>
            ))}
        </div>
      )}
    </div>
  );
};

ChatBotPromptSelector.propTypes = {
  /** Array of available prompts */
  prompts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      tooltip: PropTypes.string,
    }),
  ).isRequired,
  /** Function called when a prompt is selected */
  onPromptSelect: PropTypes.func.isRequired,
  /** Whether the selector is disabled */
  disabled: PropTypes.bool,
  /** Additional CSS class name */
  className: PropTypes.string,
};

export default ChatBotPromptSelector;
